import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ArrowUpRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { WHATS_NEW, TAG_STYLE, fmtNewsDate } from "@/data/whatsNew";
import { getCatalog, getWhatsNew } from "@/lib/api";

export default function WhatsNew() {
  const [newTools, setNewTools] = useState([]);
  const [items, setItems] = useState(WHATS_NEW);
  useEffect(() => {
    window.scrollTo(0, 0); document.title = "What's New | WebsiteTech.pro";
    getWhatsNew().then((d) => { if (d?.items?.length) setItems(d.items); }).catch(() => {});
    getCatalog().then((d) => {
      const items = (d.sections || []).flatMap((s) => s.tools.filter((t) => t.isNew).map((t) => ({ ...t, section: s.title })));
      setNewTools(items);
    }).catch(() => {});
  }, []);

  return (
    <>
      <Navbar />
      <main data-testid="whats-new-page" className="bg-slate-50">
        <section className="pt-28 pb-10 bg-white border-b border-slate-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] text-brand"><Sparkles size={13} /> What's New</span>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 mt-4">New tools &amp; updates</h1>
            <p className="text-slate-500 mt-3 text-lg max-w-2xl">The latest features we've shipped — and everything newly available across the platform.</p>
          </div>
        </section>

        {/* Timeline feed */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="relative border-l-2 border-slate-200 ml-2 pl-6 space-y-8" data-testid="whats-new-feed">
            {items.map((n, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: 12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: (i % 4) * 0.05 }} className="relative">
                <span className="absolute -left-[31px] top-1.5 h-3.5 w-3.5 rounded-full bg-brand ring-4 ring-blue-50" />
                <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-5" data-testid={`news-item-${i}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${TAG_STYLE[n.tag] || "bg-slate-100 text-slate-600"}`}>{n.tag}</span>
                    <span className="text-xs text-slate-400">{fmtNewsDate(n.date)}</span>
                  </div>
                  <h3 className="font-heading font-bold text-slate-900 text-lg mt-2">{n.title}</h3>
                  <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{n.desc}</p>
                  {n.to && <Link to={n.to} className="inline-flex items-center gap-1 text-sm font-semibold text-brand mt-3 hover:gap-2 transition-all">Check it out <ArrowRight size={14} /></Link>}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Auto: all new tools from catalog */}
        {newTools.length > 0 && (
          <section className="py-14 bg-white border-t border-slate-100" data-testid="whats-new-tools">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">Newly available tools</h2>
              <p className="text-slate-500 mt-1.5">Auto-surfaced from our catalog — every tool currently marked new.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
                {newTools.map((t) => {
                  const Card = t.to ? Link : "div";
                  return (
                    <Card key={t.name} {...(t.to ? { to: t.to } : {})} className="bg-slate-50 border border-slate-200 rounded-xl p-5 hover:border-slate-300 transition-colors group block">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold text-slate-900">{t.name}</h3>
                        {t.to && <ArrowUpRight size={16} className="text-slate-400 group-hover:text-brand shrink-0" />}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5">{t.section}</div>
                      <p className="text-sm text-slate-500 mt-2 leading-relaxed">{t.desc}</p>
                    </Card>
                  );
                })}
              </div>
              <div className="mt-8"><Link to="/tools" className="inline-flex items-center gap-2 font-semibold rounded-xl px-6 py-3 bg-brand text-white">See all tools &amp; packages <ArrowRight size={16} /></Link></div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}
